import React from 'react';
import moment from 'moment';
import {useSelector} from 'react-redux';
import {DailyDateInfo, DateInfo, Holiday} from '../types';
import {CurrentDateFlag, DateIndicatorFlag, GridLine, HeaderSection, Milestone, MilestoneFlag, StartDateFlag, EndDateFlag} from './GridComponents';
import {defaultWorkingDays, isOrgWeekend} from '../../../utils/helperUtils';
import {useTimelineMouseTracking} from '../hooks/useTimelineMouseTracking';
import {ENTITY} from "../../../redux/slices/timeline";

interface YearlyGridViewProps {
  startDate: string;
  endDate: string;
  spacing: number;
  totalHeight: number;
  milestones?: Milestone[];
}

const YearlyGridView: React.FC<YearlyGridViewProps> = ({
  startDate,
  endDate,
  spacing,
  totalHeight,
  milestones
}) => {
  const {
    orgSchedule: organizationHolidays = [],
  } = useSelector((state: any) => state.organization);

  const {
    currentEntity,
  } = useSelector((state: any) => state.timeline);

  const getAllDatesForYear = () => {
    // Start from the half-year containing startDate: Jan 1 if H1, Jul 1 if H2
    const rawStart = moment(startDate);
    const start = rawStart.month() >= 6
      ? rawStart.clone().startOf('year').add(6, 'months')
      : rawStart.clone().startOf('year');

    // Default end at the end of the year of endDate
    const defaultEffectiveEndDate = moment(endDate).endOf('year');

    // Ensure at least 12 months are visible from the aligned start
    const monthsBetween = defaultEffectiveEndDate.diff(start, 'months') + 1; // inclusive
    const effectiveEndDate = monthsBetween < 12
      ? start.clone().add(12, 'months').subtract(1, 'day')
      : defaultEffectiveEndDate;

    const allDates: DailyDateInfo[] = [];
    const current = moment(start);
    while (current.isSameOrBefore(effectiveEndDate)) {
      allDates.push({
        date: current.format('YYYY-MM-DD'),
        isWeekend: false,
        isHoliday: false,
        nextDayIsWeekend: false,
        nextDayIsHoliday: false,
        holidayName: '',
        holidayGroup: null,
        quarter: '',
        year: current.format('YYYY')
      });
      current.add(1, 'day');
    }
    return { dates: allDates };
  };

  const { dates: yearlyDates } = getAllDatesForYear();
  const sectionWidth = spacing;

  const { isMouseInTimeline, mousePosition, topPosition, mouseHandlers } = useTimelineMouseTracking({
    dates: yearlyDates,
    spacing: sectionWidth,
    viewType: 'yearly'
  });

  const holidayDates = new Set(
    organizationHolidays.map((h: Holiday) => (h?.date || '').split('T')[0])
  );
  const isWorkingDay = (dateStr: string) => {
    const weekend = isOrgWeekend(dateStr, defaultWorkingDays as unknown as number[]);
    const isHoliday = holidayDates.has(dateStr);
    return !weekend && !isHoliday;
  };
  const getNextWorkingDay = (d: string) => {
    let cur = moment(d).add(1, 'day');
    while (true) {
      const ds = cur.format('YYYY-MM-DD');
      if (isWorkingDay(ds)) return ds;
      cur = cur.add(1, 'day');
    }
  };

  const datesByYear = yearlyDates.reduce((acc: { [key: string]: { [key: string]: DateInfo[] } }, date) => {
    const year = moment(date.date).format('YYYY');
    const month = moment(date.date).format('MMM');

    if (!acc[year]) {
      acc[year] = {};
    }
    if (!acc[year][month]) {
      acc[year][month] = [];
    }
    acc[year][month].push(date);

    return acc;
  }, {});

  const monthBoundaries = new Set<number>();
  let currentPosition = 0;

  Object.entries(datesByYear).forEach(([year, months]) => {
    Object.entries(months).forEach(([month, dates]) => {
      currentPosition += dates.length * sectionWidth;
      monthBoundaries.add(currentPosition);
    });
  });

  return (
    <div className="absolute top-0" style={{ height: `max(${totalHeight}px, 100%)` }}>
      <div className="sticky top-0 z-40 bg-gray-50 dark:bg-[#111928]">
        {/* Years */}
        <div className="flex bg-white dark:bg-[#1a1f2e]">
          {Object.entries(datesByYear).map(([year, months], index) => {
            const daysInYear = Object.values(months).flat().length;
            const yearWidth = daysInYear * sectionWidth;
            return (
              <HeaderSection
                key={year}
                label={year}
                width={yearWidth}
                index={index}
              />
            );
          })}
        </div>

        {/* Months */}
        <div className="flex bg-white dark:bg-[#1a1f2e]">
          {Object.entries(datesByYear).map(([year, months]) => (
            Object.entries(months).map(([month, dates], monthIndex) => {
              const monthWidth = dates.length * sectionWidth;
              return (
                <HeaderSection
                  key={`${year}-${month}`}
                  label={month}
                  width={monthWidth}
                  index={monthIndex}
                />
              );
            })
          ))}
        </div>
      </div>

      {/* Grid lines */}
      <div 
        className="absolute top-0 left-0 w-full"
        style={{ height: `max(${totalHeight}px, calc(100vh - 119px))` }}
        {...mouseHandlers}
      >
        {yearlyDates.map((date, index) => {
          const position = index * sectionWidth;
          const isMonthBoundary = monthBoundaries.has(position + sectionWidth);
          const isWeekBoundary = (index + 1) % 4 === 0; // Every 4th column

          const milestonesForDate = milestones?.filter(milestone => {
            const milestoneDate = moment(milestone.start_date).format('YYYY-MM-DD');
            return milestoneDate === moment(date.date).format('YYYY-MM-DD');
          }) || [];
          
          const isToday = moment(date.date).format('YYYY-MM-DD') === moment().format('YYYY-MM-DD');
          const isStart = moment(date.date).format('YYYY-MM-DD') === moment(startDate).format('YYYY-MM-DD');
          const endFlagTargetDate = getNextWorkingDay(endDate);
          const isEnd = moment(date.date).format('YYYY-MM-DD') === endFlagTargetDate;

          return (
            <div key={`yearly-grid-${date.date}`}>
              <GridLine
                left={index * sectionWidth}
                width={sectionWidth}
                height="100%"
                viewType="yearly"
                isMonthBoundary={isMonthBoundary}
                isWeekend={isWeekBoundary} // Repurposing isWeekend for week boundaries
              />

              {currentEntity === ENTITY.ROADMAP && milestonesForDate.map((milestone, milestoneIndex) => (
                <MilestoneFlag
                  key={`milestone-${milestone.id}-${milestoneIndex}`}
                  milestone={milestone}
                  position={{
                    left: `${index * sectionWidth}px`,
                    top: '72px'
                  }}
                />
              ))}

              {isToday && (
                <CurrentDateFlag
                  position={{
                    left: `${index * sectionWidth}px`,
                    top: '66.5px'
                  }}
                />
              )}

              {isStart && (
                <StartDateFlag
                  date={startDate}
                  position={{
                    left: `${index * sectionWidth}px`,
                    top: '66.5px'
                  }}
                />
              )}

              {isEnd && (
                <EndDateFlag
                  originalDate={endDate}
                  position={{
                    left: `${index * sectionWidth}px`,
                    top: '72px'
                  }}
                />
              )}

              {isMouseInTimeline && mousePosition && (
                <DateIndicatorFlag
                  date={mousePosition.date}
                  left={mousePosition.x}
                  viewType="yearly"
                  top={topPosition}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default YearlyGridView; 